// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  ChargeFilter,
  getCharges,
  serializeChargeFilter,
  serializeChargeSummaryScope,
} from '@/billing/api/get-charges';
import { TWO_MINUTES_MS } from '@/utilities/time-constants';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

interface UseChargesOptions {
  filter?: ChargeFilter;
}

export function useCharges(options: UseChargesOptions = {}) {
  const filter = options.filter ?? { pageSize: 20 };
  const serializedFilter = serializeChargeFilter(filter);
  // The summary scope is a subset of the filter that only includes
  // customer, project, bill date, and amount range — not status, search,
  // sort, or page. Consumers use this to know when the summary-affecting
  // filters changed.
  const summaryScope = serializeChargeSummaryScope(filter);

  const { data, isLoading, isFetching, isPlaceholderData } = useQuery({
    queryKey: ['charges', 'list', serializedFilter],
    queryFn: () => getCharges(filter),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: true,
    refetchInterval: TWO_MINUTES_MS,
  });

  // `charges` and `totalCount` are `null` while the first fetch is in
  // flight. Consumers must treat `null` as "not yet known" and render a
  // skeleton — surfacing `[]` / `0` here fabricates an empty result set and
  // causes the listing to flash an empty state, "0 items" export label, or
  // "1-0 of 0" footer before the real data arrives.
  return {
    charges: data?.results ?? null,
    totalCount: data?.totalCount ?? null,
    // `null` while loading so consumers can render a skeleton instead of
    // an empty / zero summary. An empty array would look identical to
    // "org has no charges" once the fetch returns.
    statusSummary: data?.statusSummary ?? null,
    amountRange: data?.amountRange ?? null,
    summaryScope,
    isLoading,
    isFetching,
    isPlaceholderData,
  };
}
